import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EventCard from './EventCard';
import styles from './onThisDayStyles';
import { mergeMultipleApiData, getFallbackSportsData } from './onThisDayUtils';
import {
  fetchWikipediaAllEndpoints,
  fetchWikipediaEnhanced,
  fetchZenQuotesData,
  fetchMuffinLabsData,
  fetchDayInHistoryData,
} from './onThisDayApi';

const OnThisDay = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [sportsData, setSportsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('events');
  const [selectedSport, setSelectedSport] = useState('all');
  const [showFloatingButton, setShowFloatingButton] = useState(false);

  // NEW: Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    fetchSportsHistory(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    const handleScroll = () => {
      setShowFloatingButton(window.scrollY > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchSportsHistory = async (date) => {
    setLoading(true);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const cacheKey = `sports_ultra_${month}_${day}`;

    // Check cache
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const cachedData = JSON.parse(cached);
        const cacheAge = Date.now() - cachedData.timestamp;
        if (cacheAge < 3600000) {
          setSportsData(cachedData.data);
          setLoading(false);
          return;
        }
      }
    } catch (error) {
      console.error('Cache read error:', error);
    }

    try {
      // Parallel API calls from 5 different sources for maximum coverage
      const apiPromises = [
        fetchWikipediaAllEndpoints(month, day),
        fetchZenQuotesData(month, day),
        fetchWikipediaEnhanced(month, day),
        fetchMuffinLabsData(month, day),
        fetchDayInHistoryData(month, day)
      ];

      const results = await Promise.allSettled(apiPromises);
      
      const allData = results
        .filter(result => result.status === 'fulfilled' && result.value)
        .map(result => result.value);

      // Merge all data sources
      const mergedData = mergeMultipleApiData(allData);
      
      if (mergedData.events.length > 0 || mergedData.births.length > 0 || mergedData.deaths.length > 0) {
        setSportsData(mergedData);
        
        // Cache result
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({
            data: mergedData,
            timestamp: Date.now()
          }));
        } catch (error) {
          console.error('Cache write error:', error);
        }
      } else {
        setSportsData(getFallbackSportsData(month, day));
      }
    } catch (error) {
      console.error('Error fetching sports history:', error);
      setSportsData(getFallbackSportsData(month, day));
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    const newDate = new Date(e.target.value);
    setSelectedDate(newDate);
  };

  const formatDate = (date) => {
    const options = { month: 'long', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const getFilteredData = () => {
    if (!sportsData) return [];
    
    let data = [];
    if (activeTab === 'events') data = sportsData.events || [];
    if (activeTab === 'births') data = sportsData.births || [];
    if (activeTab === 'deaths') data = sportsData.deaths || [];

    if (selectedSport === 'all') return data;
    return data.filter(item => item.sport === selectedSport);
  };

  const sports = [
    { key: 'all', name: 'All Sports', icon: '🏆' },
    { key: 'football', name: 'Football', icon: '⚽' },
    { key: 'tennis', name: 'Tennis', icon: '🎾' },
    { key: 'cricket', name: 'Cricket', icon: '🏏' },
    { key: 'basketball', name: 'Basketball', icon: '🏀' },
    { key: 'baseball', name: 'Baseball', icon: '⚾' },
    { key: 'other', name: 'Other', icon: '🏅' }
  ];

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div className="mobile-header-top">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-xs px-3 py-1.5 rounded transition-all"
            style={{ backgroundColor: '#fff' }}
          >
            <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="sm:hidden">Back</span>
            <span className="hidden sm:inline">Back to Home</span>
          </button>
        </div>
        <button
          onClick={() => navigate('/')}
          className="desktop-back-btn inline-flex items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-all"
          style={{ backgroundColor: '#fff' }}
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="sm:hidden">Back</span>
          <span className="hidden sm:inline">Back to Home</span>
        </button>
        <div style={styles.headerContent} className="header-content-responsive">
          <h1 style={styles.title}>Sports History</h1>
          <p style={styles.subtitle}>Discover legendary moments in sports history</p>
        </div>
      </header>

      {showFloatingButton && (
        <button 
          onClick={() => navigate('/')} 
          style={styles.floatingButton}
          className="floating-back-btn"
        >
          <span className="sm:hidden">Back</span>
          <span className="hidden sm:inline">Back to Home</span>
        </button>
      )}

      <div style={styles.dateCard} className="date-card-responsive">
        <div style={styles.dateDisplay}>
          <span style={styles.dateIcon}>📅</span>
          <h2 style={styles.dateTitle} className="date-title-responsive">{formatDate(selectedDate)}</h2>
        </div>
        <input
          type="date"
          value={selectedDate.toISOString().split('T')[0]}
          onChange={handleDateChange}
          style={styles.datePicker}
          className="date-picker-responsive"
          max={new Date().toISOString().split('T')[0]}
        />
      </div>

      <div style={styles.tabContainer} className="tab-container-responsive">
        <button
          onClick={() => setActiveTab('events')}
          style={{
            ...styles.tab,
            ...(activeTab === 'events' ? styles.activeTab : {})
          }}
          className="tab-responsive"
        >
          🏆 Historic Events ({sportsData?.events.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('births')}
          style={{
            ...styles.tab,
            ...(activeTab === 'births' ? styles.activeTab : {})
          }}
          className="tab-responsive"
        >
          🎂 Player Birthdays ({sportsData?.births.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('deaths')}
          style={{
            ...styles.tab,
            ...(activeTab === 'deaths' ? styles.activeTab : {})
          }}
          className="tab-responsive"
        >
          🕊️ Remembering Legends ({sportsData?.deaths.length || 0})
        </button>
      </div>

      <div style={styles.content} className="content-responsive">
        {loading ? (
          <div style={styles.loading}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>Loading sports history...</p>
          </div>
        ) : (
          <div style={styles.eventsList}>
            {getFilteredData().length > 0 ? (
              getFilteredData().map((item, index) => (
                <EventCard key={`${item.year}-${index}`} data={item} type={activeTab} />
              ))
            ) : (
              <div style={styles.noData}>
                <span style={styles.noDataIcon}>📭</span>
                <p style={styles.noDataText}>No sports events found for this date and filter</p>
                <button 
                  onClick={() => setSelectedSport('all')}
                  style={styles.resetButton}
                  className="reset-button-responsive"
                >
                  Show All Sports
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @keyframes slideInUp {
    from {
      transform: translateY(100px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (hover: hover) {
    button:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    }

    .floating-back-btn:hover {
      transform: scale(1.05);
      box-shadow: 0 6px 25px rgba(0,123,255,0.5);
    }

    .event-card-responsive:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 20px rgba(0,0,0,0.12) !important;
    }

    .link-responsive:hover {
      background-color: #007bff !important;
      color: #ffffff !important;
      transform: translateY(-1px);
    }

    .date-picker-responsive:hover {
      border-color: #007bff;
    }
  }

  @media (max-width: 1024px) {
    .date-card-responsive {
      margin: 15px 20px !important;
    }

    .tab-container-responsive {
      padding: 0 15px !important;
    }

    .content-responsive {
      padding: 0 15px !important;
    }
  }

  @media (max-width: 768px) {
    .desktop-back-btn {
      display: none !important;
    }

    .mobile-header-top {
      display: flex !important;
      justify-content: flex-start !important;
      margin-bottom: 12px !important;
    }

    .header-content-responsive {
      text-align: center !important;
    }

    .date-card-responsive {
      flex-direction: column !important;
      align-items: flex-start !important;
      margin: 15px 10px !important;
      padding: 15px !important;
      gap: 12px !important;
    }

    .date-title-responsive {
      font-size: 20px !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
    }

    .date-picker-responsive {
      width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }

    .tab-container-responsive {
      flex-direction: column !important;
      padding: 0 10px !important;
      gap: 10px !important;
    }

    .tab-responsive {
      width: 100% !important;
      min-width: 100% !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
      white-space: normal !important;
      word-wrap: break-word !important;
      overflow: visible !important;
      padding: 10px 12px !important;
      line-height: 1.3 !important;
    }

    .content-responsive {
      padding: 0 10px !important;
      box-sizing: border-box !important;
      max-width: 100% !important;
      overflow-x: hidden !important;
    }

    .event-card-responsive {
      flex-direction: column !important;
      padding: 15px !important;
      box-sizing: border-box !important;
      max-width: 100% !important;
      overflow: hidden !important;
    }

    .event-left-responsive {
      flex-direction: row !important;
      width: 100%;
      justify-content: flex-start;
      gap: 15px !important;
      flex-wrap: wrap !important;
    }

    .sport-badge-responsive {
      font-size: 28px !important;
      padding: 6px !important;
    }

    .year-badge-responsive {
      font-size: 14px !important;
      white-space: nowrap !important;
    }

    .event-center-responsive {
      width: 100%;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }

    .event-icon-responsive {
      font-size: 20px !important;
    }

    .event-text-responsive {
      font-size: 15px !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      word-break: break-word !important;
      max-width: 100% !important;
    }

    .link-responsive {
      font-size: 12px !important;
      padding: 5px 10px !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }

    .floating-back-btn {
      bottom: 20px !important;
      right: 20px !important;
      padding: 12px 20px !important;
      font-size: 14px !important;
    }
  }

  @media (min-width: 769px) {
    .mobile-header-top {
      display: none !important;
    }
  }

  @media (max-width: 480px) {
    h1 {
      font-size: 22px !important;
      padding: 0 5px !important;
    }

    .subtitle {
      font-size: 13px !important;
      padding: 0 5px !important;
    }

    .date-icon {
      font-size: 24px !important;
    }

    .date-title-responsive {
      font-size: 17px !important;
      word-wrap: break-word !important;
    }

    .date-picker-responsive {
      font-size: 13px !important;
      padding: 8px 10px !important;
      width: 100% !important;
    }

    .tab-responsive {
      padding: 8px 12px !important;
      font-size: 13px !important;
      white-space: normal !important;
      line-height: 1.4 !important;
    }

    .event-card-responsive {
      padding: 12px !important;
      margin: 0 !important;
    }

    .sport-badge-responsive {
      font-size: 24px !important;
      padding: 5px !important;
    }

    .year-badge-responsive {
      font-size: 12px !important;
      padding: 4px 8px !important;
    }

    .event-text-responsive {
      font-size: 13.5px !important;
      line-height: 1.5 !important;
    }

    .floating-back-btn {
      bottom: 15px !important;
      right: 15px !important;
      padding: 10px 16px !important;
      font-size: 13px !important;
    }

    .reset-button-responsive {
      padding: 10px 20px !important;
      font-size: 14px !important;
      width: 100% !important;
      max-width: 200px !important;
    }

    .no-data-icon {
      font-size: 48px !important;
    }

    .no-data-text {
      font-size: 15px !important;
      padding: 0 10px !important;
    }
  }

  @media (max-width: 360px) {
    h1 {
      font-size: 22px !important;
    }

    .date-title-responsive {
      font-size: 16px !important;
    }

    .tab-responsive {
      font-size: 13px !important;
    }

    .event-text-responsive {
      font-size: 13px !important;
    }

    .floating-back-btn {
      padding: 8px 14px !important;
      font-size: 12px !important;
    }
  }

  @media (max-height: 500px) and (orientation: landscape) {
    .floating-back-btn {
      bottom: 10px !important;
      right: 10px !important;
      padding: 8px 16px !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default OnThisDay;
